import React, { useState } from "react";
import axios from "axios";
import { RxCross1 } from "react-icons/rx";
import { BASE_URL } from "utils/constant";
import SampleInventoryLibrary from "assets/sample_sheets/Sample_Inventory.library.xlsx";

const MaterialLibraryUploadModal = ({ onClose, organizationId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMessage, setUploadMessage] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const endpoint = `${BASE_URL}/org/material-library/bulk-upload/`;

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedFile(file);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    const fileInput = document.getElementById("material-library-file-upload");
    if (fileInput) fileInput.value = "";
  };

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // show as copy
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setUploadMessage("Please select a file to upload");
      return;
    }
    setIsLoading(true);
    setUploadMessage("");
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (organizationId) {
        formData.append("organization", organizationId);
      }
      const response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });
      setUploadMessage("File successfully uploaded");
      // reset file
      setSelectedFile(null);
      setTimeout(() => onClose?.(), 1200);
      return response?.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      setUploadMessage(`Error uploading file: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSheetLink = async () => {
    const linkInput = document.querySelector("#material-library-sheet-link");
    const link = linkInput?.value?.trim();
    if (!link) {
      setUploadMessage("Please enter a valid Google Sheet link");
      return;
    }
    setIsLoading(true);
    setUploadMessage("");
    try {
      const response = await axios.post(
        endpoint,
        {
          organization: organizationId,
          sheet_url: link,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        }
      );
      setUploadMessage("Google Sheet successfully processed");
      if (linkInput) linkInput.value = "";
      setTimeout(() => onClose?.(), 1200);
      return response?.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      setUploadMessage(`Error processing Google Sheet: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg w-[560px] p-6"
        style={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Upload materials (library)</h3>
          <RxCross1
            className="text-[#232323] w-5 h-5 cursor-pointer"
            onClick={onClose}
          />
        </div>

        {!selectedFile ? (
          <div
            className={`border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6 ${dragActive ? "bg-purple-50 border-purple-400" : ""
              }`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="text-center">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="material-library-file-upload"
                disabled={isLoading}
              />
              <label
                htmlFor="material-library-file-upload"
                className={`flex flex-col items-center ${isLoading ? "cursor-not-allowed opacity-70" : "cursor-pointer"
                  }`}
              >
                <svg
                  className="w-8 h-8 text-gray-400 mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                <span className="text-gray-600">Click to browse files</span>
                <span className="text-xs text-gray-400 mt-2">or drag & drop file here</span>
              </label>
            </div>
          </div>
        ) : (
          <div className="border-2 border-gray-300 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg
                  className="w-6 h-6 text-gray-500 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-800 truncate" title={selectedFile.name}>
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <button
                onClick={removeSelectedFile}
                className="ml-2 text-gray-500 hover:text-red-500"
                title="Remove file"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <p className="text-gray-600"></p>
            <a
              href={SampleInventoryLibrary}
              download
              className="text-[#58267F] font-medium flex items-center hover:underline"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download Sample
            </a>
          </div>
          {/* <input
            type="text"
            id="material-library-sheet-link"
            placeholder="Paste Google Sheet link here"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            disabled={isLoading}
          /> */}
        </div>

        {uploadMessage && (
          <div
            className={`p-3 mb-4 rounded-lg ${uploadMessage.includes("Error")
              ? "bg-red-100 text-red-700"
              : "bg-green-100 text-green-700"
              }`}
          >
            {uploadMessage}
          </div>
        )}

        <div className="flex gap-3">
          {selectedFile && (
            <button
              onClick={handleFileUpload}
              className={`flex-1 bg-[#3B0069] text-white py-3 rounded-lg hover:bg-purple-800 transition-colors flex items-center justify-center ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>Upload File</span>
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </>
              )}
            </button>
          )}
          <button
            onClick={handleGoogleSheetLink}
            className={`flex-1 bg-[#3B0069] text-white py-3 rounded-lg hover:bg-purple-800 transition-colors flex items-center justify-center ${isLoading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>Upload Sheet</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialLibraryUploadModal;


